/**
 * Behavioural signal for the release loop: who saw an announcement, who
 * engaged with it, and who closed it on sight - including the majority who
 * will never type anything back. That silent group is the point; they are the
 * accounts most likely to be drifting, and this is the only thing they tell us
 * without being asked.
 *
 * Sent to our OWN Encharge account through the tracking snippet the app
 * already loads and identifies into (see accountStore), so behaviour lands on
 * the same person profile as anything they write, and we can segment on it.
 *
 * Fire-and-forget: the snippet may not have loaded yet, may be blocked, or the
 * visitor may not have opted in. None of that is worth a broken banner.
 */
export type AnnouncementAction =
  | "seen"
  | "clicked"
  | "dismissed"
  | "dialog opened"
  | "dialog closed";

/**
 * Which path produced the event: for "dialog opened", how it was opened; for
 * "clicked", what the CTA did.
 *
 * The two actions stopped being alternatives when the dialog stopped opening
 * by itself. "clicked" is now the banner CTA whatever it does, and "dialog
 * opened" is a dialog reaching the screen however it got there - two levels of
 * one funnel, so `seen >= clicked >= dialog opened from the banner` holds by
 * construction. Without the source we would have the count of dialog views but
 * no longer know whether people chose to open it or were shown it, which is
 * the question turning autoOpen off was meant to answer.
 *
 * Left off the actions that have exactly one path, so those events do not
 * carry an empty property on the person profile.
 */
export type AnnouncementSource = "auto" | "banner" | "menu" | "link" | "dialog";

export const trackAnnouncement = (
  action: AnnouncementAction,
  announcementId: string,
  source?: AnnouncementSource
) => {
  try {
    const win = window as any;
    win?.EncTracking?.track?.({
      name: `Feature announcement ${action}`,
      properties: { announcementId, ...(source ? { source } : {}) },
    });
  } catch (e) {
    // tracking must never take the app down with it
  }
};
